<template>
    <transition name="cz3__fade">
        <div
            v-if="showModal"
            @click.self="emitClose"
            class="z-20 fixed top-0 left-0 w-full h-full flex flex-col justify-center items-center bg-black bg-opacity-30"
        >
            <div
                :class="[ showMatchingSetForm ? 'max-w-3xl' : 'max-w-xl overflow-hidden' ]"
                class="relative shadow-md rounded flex flex-col max-h-full w-full sm:w-11/12 p-3 sm:p-9 bg-white font-oceanside text-black"
            >
                <header class="sticky flex justify-between items-center">
                    <h1 class="text-lg font-semibold capitalize">
                        {{ modalHeading }}
                    </h1>
                </header>

                <div
                    v-auto-animate
                    class="flex-1 overflow-y-auto"
                >
                    <div
                        v-if="showMatchingSetForm"
                        ref="matchingSetForm"
                        v-auto-animate
                        :class="{ 'py-4' : cart.length }"
                    >
                        <cart-modal-items
                            v-if="cart.length"
                            @edit="editClub"
                            @remove="removeClub"
                        />
                    </div>

                    <p
                        v-else
                        ref="addedToCartCopy"
                        class="pb-9 text-sm"
                    >
                        {{ updated ? $t('UPDATED_CART_COPY') : $t('ADDED_TO_CART_COPY') }}
                    </p>
                </div>

                <div
                    v-auto-animate
                    class="sticky bottom-0 flex flex-col justify-center gap-4"
                >
                    <div v-if="showMatchingSetForm">
                        <div class="mt-6">
                            <label class="mb-1 text-base font-oceanside font-medium">
                                {{ $t('SELECT_A_LOFT') }}
                            </label>

                            <base-input-select
                                :select-options="loftOptions"
                                @input="addClubToCart``"
                                label="label"
                                class="w-full"
                            />
                        </div>

                        <p class="font-semibold text-red-error mt-4 mb-6">
                            {{ $t('TOTAL_PRICE') }}: {{ cartPrice }}
                        </p>
                    </div>

                    <div class="w-full flex flex-col sm:flex-row justify-center gap-4">
                        <!--
                        <base-button-flat
                            v-if="showMatchingSetForm"
                            @click="cancelMatchingSet"
                            variant="secondary"
                            class="w-full sm:flex-1 h-10 px-3 uppercase"
                        >
                            {{ $t('CANCEL') }}
                        </base-button-flat>
                        -->

                        <base-button-flat
                            v-if="canMakeSet && !showMatchingSetForm"
                            @click="makeMatchingSet"
                            variant="secondary"
                            class="w-full sm:flex-1 h-10 px-3 uppercase"
                        >
                            {{ $t('MAKE_MATCHING_SET') }}
                        </base-button-flat>

                        <base-button-flat
                            @click="goToCart"
                            class="w-full sm:flex-1 h-10 uppercase"
                        >
                            {{ $t('GO_TO_CART') }}
                        </base-button-flat>
                    </div>
                </div>
            </div>

            <auto-modal
                v-if="addToCartError"
                :text="$t('UNEXPECTED_ERROR')"
                @close="closeAddToCartError"
            />
        </div>
    </transition>
</template>

<script>
    import allStores from '../stores/all';

    import BaseButtonFlat from './buttons/BaseButtonFlat.vue';
    import BaseInputSelect from './BaseInputSelect.vue';
    import CartModalItems from './CartModalItems.vue';
    import AutoModal from './AutoModal.vue';

    export default {
        mixins: [allStores],

        components: {
            BaseButtonFlat,
            BaseInputSelect,
            CartModalItems,
            AutoModal,
        },

        props: {
            showModal: {
                type: Boolean,
                default: false,
            },
            updated: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                showMatchingSetForm: false,

                canAddToCart: false,

                addToCartError: false,
            };
        },

        computed: {
            viewController() {
                return this.customizerStore.viewController;
            },

            baseController() {
                return this.customizerStore.clubSetController;
            },

            canMakeSet() {
                return this.baseController != null;
            },

            modalHeading() {
                return this.showMatchingSetForm ? `${this.$t('MAKE_MATCHING_SET')}` : `${this.$t('ADDED_TO_CART_HEADING')}`;
            },

            productPrice() {
                return this.customizerStore.productPrice;
            },

            loftOptions() {
                const other = [];

                // Filter based on the cart.
                const alreadyAdded = new Set();

                // eslint-disable-next-line no-restricted-syntax
                for (const item of this.cart) {
                    alreadyAdded.add(item.controller.blueprint.styleCode.replace('WV', ''));
                }

                if (this.viewController?.blueprint?.custom?.['v-set']) {
                    const set = JSON.parse(this.viewController?.blueprint?.custom?.['v-set']);

                    if (set) {
                        set.forEach((s) => {
                            if (!alreadyAdded.has(s.id.toString())) {
                                other.push(s);
                            }
                        });
                    }
                }

                other.sort((a, b) => {
                    const labelA = a.label;
                    const labelB = b.label;

                    if (labelA < labelB) {
                        return -1;
                    }

                    if (labelA > labelB) {
                        return 1;
                    }

                    return 0;
                });

                return other;
            },

            cart() {
                return this.customizerStore.cart;
            },

            cartPrice() {
                return this.customizerStore.cartPrice;
            },
        },

        watch: {
            showModal() {
                if (this.showModal) {
                    if (this.customizerStore.cart.length > 1) {
                        this.showMatchingSetForm = true;
                    }
                }
            },
        },

        methods: {
            emitClose() {
                this.$emit('modalClose');

                this.showMatchingSetForm = false;
            },

            goToCart() {
                this.appStore.setIsBusy(true);

                if (this.customizerStore.options.onGoToCart) {
                    this.customizerStore.options.onGoToCart();
                } else {
                    // Redirect to cart.
                    let cartUrl = this.viewController.blueprint.custom['v-cart-url'];

                    if (this.customizerStore.options.cartUrl) {
                        cartUrl = this.customizerStore.options.cartUrl;
                    }

                    if (cartUrl) {
                        window.location.href = cartUrl;
                    }
                }

                // Stay open to avoid the screen flash.
            },

            makeMatchingSet() {
                this.showMatchingSetForm = true;
            },

            cancelMatchingSet() {
                this.showMatchingSetForm = false;
                this.canAddToCart = false;

                // Remove "matching" clubs from cart
                this.customizerStore.removeSetFromCart();
            },

            addItemBackToList(club) {
                this.loftOptions.push(club);
            },

            addClubToCart(value) {
                this.customizerStore.addClubToCart(value, this.baseController).then(() => {
                }).catch(() => {
                    this.addToCartError = true;
                });
            },

            editClub(item) {
                this.customizerStore.editInstance(item);

                // Close the modal for now...
                this.emitClose();
            },

            removeClub(item) {
                this.customizerStore.removeClubFromCart(item).then(() => {
                    // Nothing special.
                }).catch(() => {
                    this.addToCartError = true;
                });
            },

            addToCart() {
                this.customizerStore.addToCart().then(() => {
                    this.cancelMatchingSet();
                });
            },

            closeAddToCartError() {
                this.addToCartError = false;
            },
        },
    };
</script>
